import { useCallback, useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'] as const;

export interface UseBellringerInactivityArgs {
  /** Run the timer only while true (e.g. bellringer mode AND the nudge not open). */
  enabled: boolean;
  /** Idle duration before `onTimeout` fires. */
  timeoutMs: number;
  /** Called once when the idle duration elapses with no user activity. */
  onTimeout: () => void;
}

/**
 * Fires `onTimeout` after `timeoutMs` of no user activity while `enabled`.
 * Any click / keydown / mousemove / scroll resets the countdown. The timer and
 * its listeners are torn down when `enabled` goes false or the component unmounts.
 *
 * Callers should set `enabled` to false while the resulting nudge dialog is open
 * so the timeout does not fire again; flipping it back to true (dialog resolved,
 * or a fresh session after a switch) restarts a clean countdown.
 */
export const useBellringerInactivity = ({
  enabled,
  timeoutMs,
  onTimeout,
}: UseBellringerInactivityArgs): void => {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    clear();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onTimeoutRef.current();
    }, timeoutMs);
  }, [clear, timeoutMs]);

  useEffect(() => {
    if (!enabled) {
      clear();
      return undefined;
    }
    schedule();
    const handleActivity = () => schedule();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );
    return () => {
      clear();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [enabled, schedule, clear]);
};
